import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FaChartLine, FaEuroSign, FaShoppingCart, FaUsers, FaStore, 
  FaPercent, FaArrowUp, FaArrowDown, FaClock, FaCheckCircle,
  FaTrophy, FaCalendarAlt, FaCrown, FaFileInvoice, FaTrash, FaExclamationTriangle
} from 'react-icons/fa';
import { formatPrice } from '../../utils/currency';

export default function AdminRevenues() {
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetType, setResetType] = useState('all');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/revenues`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await axios.post(`${API}/admin/dashboard/reset-counters?reset_type=${resetType}`);
      toast.success(`✅ ${response.data.message}`);
      setShowResetModal(false);
      fetchDashboard(); // Refresh data
    } catch (error) {
      console.error('Error resetting:', error);
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">Erreur de chargement des données</p>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, subtext, trend }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
          </div>
          <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('700', '100').replace('600', '100')}`}>
            <Icon className={`text-2xl ${color}`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center mt-3 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
            {Math.abs(trend)}% vs mois dernier
          </div>
        )}
      </CardContent>
    </Card>
  );

  const SimpleBarChart = ({ data, maxValue }) => {
    const max = maxValue || Math.max(...data.map(d => d.revenue), 1);
    return (
      <div className="flex items-end justify-between h-48 gap-2 px-4">
        {data.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center flex-1">
            <div className="w-full flex flex-col items-center">
              <span className="text-xs text-gray-500 mb-1">
                {item.revenue > 0 ? formatPrice(item.revenue, 'EUR') : '-'}
              </span>
              <div 
                className="w-full bg-gradient-to-t from-[#635BFF] to-[#8B7FFF] rounded-t-md transition-all duration-500"
                style={{ height: `${Math.max((item.revenue / max) * 150, 4)}px` }}
              />
            </div>
            <span className="text-xs text-gray-600 mt-2 font-medium">{item.month}</span>
          </div>
        ))}
      </div>
    );
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      pending_payment: 'Paiement requis',
      confirmed: 'Confirmée',
      shipped: 'Expédiée',
      delivered: 'Livrée',
      cancelled: 'Annulée'
    };
    return labels[status] || status;
  };

  const getPaymentLabel = (method) => {
    const labels = {
      purchase_order: 'Bon de commande',
      bank_transfer: 'Virement',
      card: 'Carte bancaire',
      cash: 'Espèces',
      unknown: 'Non défini'
    };
    return labels[method] || method;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540] flex items-center gap-3">
              <FaChartLine className="text-[#635BFF]" />
              {i18n.language === 'fr' ? 'Tableau de Bord Revenus' : 'Revenue Dashboard'}
            </h1>
            <p className="text-gray-500 mt-1">
              {i18n.language === 'fr' ? 'Vue d\'ensemble de vos performances' : 'Overview of your performance'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchDashboard} variant="outline">
              🔄 {i18n.language === 'fr' ? 'Actualiser' : 'Refresh'}
            </Button>
            <Button 
              onClick={() => setShowResetModal(true)} 
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              <FaTrash className="mr-2" />
              RAZ Test
            </Button>
          </div>
        </div>

        {/* Reset Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4 text-red-600">
                  <FaExclamationTriangle className="text-3xl" />
                  <h2 className="text-xl font-bold">⚠️ RAZ Données de Test</h2>
                </div>
                
                <p className="text-gray-600 mb-4">
                  Cette action va supprimer définitivement les données sélectionnées. 
                  <strong className="text-red-600"> Cette action est irréversible !</strong>
                </p>
                
                <div className="space-y-2 mb-6">
                  <label className="block text-sm font-medium text-gray-700">
                    Que voulez-vous réinitialiser ?
                  </label>
                  <select 
                    value={resetType} 
                    onChange={(e) => setResetType(e.target.value)}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">🗑️ TOUT (commandes, factures, devis, locations...)</option>
                    <option value="orders">📦 Commandes uniquement</option>
                    <option value="quotes">📝 Devis uniquement</option>
                    <option value="invoices">📄 Factures abonnements uniquement</option>
                    <option value="subscriptions">👑 Abonnements uniquement</option>
                    <option value="rentals">🚜 Locations uniquement</option>
                    <option value="notifications">🔔 Notifications uniquement</option>
                  </select>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowResetModal(false)}
                    className="flex-1"
                  >
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleReset}
                    disabled={resetting}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {resetting ? '⏳ Suppression...' : '🗑️ Confirmer RAZ'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title={i18n.language === 'fr' ? 'Chiffre d\'affaires total' : 'Total Revenue'}
            value={formatPrice(data.total_revenue, 'EUR')}
            icon={FaEuroSign}
            color="text-green-600"
            subtext={`${data.total_orders} commandes`}
          />
          <StatCard
            title={i18n.language === 'fr' ? `Commissions (${data.commission_rate}%)` : `Commissions (${data.commission_rate}%)`}
            value={formatPrice(data.total_commissions, 'EUR')}
            icon={FaPercent}
            color="text-[#635BFF]"
            subtext={`${formatPrice(data.paid_commissions, 'EUR')} encaissé`}
          />
          <StatCard
            title={i18n.language === 'fr' ? 'Revenus abonnements' : 'Subscription Revenue'}
            value={formatPrice(data.subscription_revenue, 'EUR')}
            icon={FaCrown}
            color="text-amber-600"
            subtext={`${data.total_subscriptions} abonnements`}
          />
          <StatCard
            title={i18n.language === 'fr' ? 'En attente' : 'Pending'}
            value={formatPrice(data.pending_revenue, 'EUR')}
            icon={FaClock}
            color="text-orange-500"
            subtext={i18n.language === 'fr' ? 'Paiements en cours' : 'Pending payments'}
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4 flex items-center gap-4">
              <FaUsers className="text-3xl text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">{data.total_users}</p>
                <p className="text-sm text-blue-600">{i18n.language === 'fr' ? 'Utilisateurs' : 'Users'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4 flex items-center gap-4">
              <FaStore className="text-3xl text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700">{data.total_shops}</p>
                <p className="text-sm text-purple-600">{i18n.language === 'fr' ? 'Boutiques' : 'Shops'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4 flex items-center gap-4">
              <FaShoppingCart className="text-3xl text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{data.total_orders}</p>
                <p className="text-sm text-green-600">{i18n.language === 'fr' ? 'Commandes' : 'Orders'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4 flex items-center gap-4">
              <FaCalendarAlt className="text-3xl text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{data.recent_orders_count}</p>
                <p className="text-sm text-amber-600">{i18n.language === 'fr' ? '30 derniers jours' : 'Last 30 days'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 rounded-lg shadow">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">
              📊 {i18n.language === 'fr' ? 'Aperçu' : 'Overview'}
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">
              📦 {i18n.language === 'fr' ? 'Commandes' : 'Orders'}
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">
              🏆 {i18n.language === 'fr' ? 'Top Fournisseurs' : 'Top Suppliers'}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Revenue Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FaChartLine className="text-[#635BFF]" />
                    {i18n.language === 'fr' ? 'Évolution du CA (6 mois)' : 'Revenue Evolution (6 months)'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart data={data.monthly_data} />
                </CardContent>
              </Card>

              {/* Orders by Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FaShoppingCart className="text-green-600" />
                    {i18n.language === 'fr' ? 'Commandes par statut' : 'Orders by Status'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(data.orders_by_status).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium">{getStatusLabel(status)}</span>
                        <Badge variant="secondary" className="text-lg px-3 py-1">
                          {count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Payment Methods */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FaFileInvoice className="text-blue-600" />
                    {i18n.language === 'fr' ? 'Modes de paiement' : 'Payment Methods'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(data.orders_by_payment_method).map(([method, count]) => (
                      <div key={method} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium">{getPaymentLabel(method)}</span>
                        <Badge variant="outline" className="text-lg px-3 py-1">
                          {count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Subscriptions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FaCrown className="text-amber-600" />
                    {i18n.language === 'fr' ? 'Abonnements par plan' : 'Subscriptions by Plan'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(data.subscriptions_by_plan).length > 0 ? (
                      Object.entries(data.subscriptions_by_plan).map(([plan, count]) => (
                        <div key={plan} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                          <span className="font-medium capitalize">{plan}</span>
                          <Badge className="bg-amber-500 text-lg px-3 py-1">
                            {count}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">Aucun abonnement</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>{i18n.language === 'fr' ? 'Détail des revenus' : 'Revenue Details'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-semibold">Métrique</th>
                        <th className="text-right p-3 font-semibold">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="p-3">💰 CA Total (commandes)</td>
                        <td className="p-3 text-right font-bold text-green-600">{formatPrice(data.total_revenue, 'EUR')}</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="p-3">✅ CA Encaissé</td>
                        <td className="p-3 text-right font-bold text-green-700">{formatPrice(data.paid_revenue, 'EUR')}</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="p-3">⏳ CA En attente</td>
                        <td className="p-3 text-right font-bold text-orange-500">{formatPrice(data.pending_revenue, 'EUR')}</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50 bg-purple-50">
                        <td className="p-3">💎 Commissions totales ({data.commission_rate}%)</td>
                        <td className="p-3 text-right font-bold text-[#635BFF]">{formatPrice(data.total_commissions, 'EUR')}</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50 bg-purple-50">
                        <td className="p-3">✅ Commissions encaissées</td>
                        <td className="p-3 text-right font-bold text-[#635BFF]">{formatPrice(data.paid_commissions, 'EUR')}</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50 bg-amber-50">
                        <td className="p-3">👑 Revenus abonnements</td>
                        <td className="p-3 text-right font-bold text-amber-600">{formatPrice(data.subscription_revenue, 'EUR')}</td>
                      </tr>
                      <tr className="bg-[#0A2540] text-white">
                        <td className="p-3 font-bold">📊 TOTAL PLATEFORME</td>
                        <td className="p-3 text-right font-bold text-xl">
                          {formatPrice(data.total_commissions + data.subscription_revenue, 'EUR')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Top Suppliers Tab */}
          <TabsContent value="suppliers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FaTrophy className="text-amber-500" />
                  {i18n.language === 'fr' ? 'Top 10 Fournisseurs par CA' : 'Top 10 Suppliers by Revenue'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.top_suppliers.length > 0 ? (
                  <div className="space-y-3">
                    {data.top_suppliers.map((supplier, idx) => (
                      <div 
                        key={supplier.id} 
                        className={`flex items-center justify-between p-4 rounded-lg ${
                          idx === 0 ? 'bg-gradient-to-r from-amber-100 to-amber-50 border-2 border-amber-300' :
                          idx === 1 ? 'bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-300' :
                          idx === 2 ? 'bg-gradient-to-r from-orange-100 to-orange-50 border border-orange-200' :
                          'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span className={`text-2xl font-bold ${
                            idx === 0 ? 'text-amber-500' :
                            idx === 1 ? 'text-gray-500' :
                            idx === 2 ? 'text-orange-500' :
                            'text-gray-400'
                          }`}>
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                          </span>
                          <div>
                            <p className="font-bold text-[#0A2540]">{supplier.name}</p>
                            <p className="text-sm text-gray-500">{supplier.orders} commandes</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">{formatPrice(supplier.revenue, 'EUR')}</p>
                          <p className="text-sm text-[#635BFF]">
                            Commission: {formatPrice(supplier.revenue * (data.commission_rate / 100), 'EUR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Aucune donnée disponible</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
